import React from "react";
import { motion } from "framer-motion";

export interface ProgressProps {
  value: number;
  max?: number;
  size?: "sm" | "md" | "lg";
  color?: "primary" | "success" | "warning" | "error" | "info";
  showValue?: boolean;
  animated?: boolean;
  className?: string;
  label?: string;
}

/**
 * Arc-style Progress/ProgressBar component
 * 
 * Features:
 * - Multiple sizes: sm, md, lg
 * - Multiple colors
 * - Optional value display
 * - Smooth animated fill
 * - Optional label
 */
const Progress: React.FC<ProgressProps> = ({
  value,
  max = 100,
  size = "md",
  color = "primary",
  showValue = false,
  animated = true,
  className = "",
  label,
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  // Size styles
  const sizeStyles = {
    sm: "h-1.5",
    md: "h-2.5",
    lg: "h-4",
  };

  // Color styles
  const colorStyles = {
    primary: "bg-accent",
    success: "bg-success",
    warning: "bg-warning-dark",
    error: "bg-error",
    info: "bg-info",
  };

  return (
    <div className={className}>
      {(label || showValue) && (
        <div className="flex items-center justify-between mb-2">
          {label && (
            <span className="text-sm font-medium text-content-primary">
              {label}
            </span>
          )}
          {showValue && (
            <span className="text-sm font-medium text-content-secondary">
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      )}
      
      <div
        className={`
          w-full
          ${sizeStyles[size]}
          bg-surface-tertiary
          rounded-arc-full
          overflow-hidden
        `}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        <motion.div
          className={`
            h-full
            ${colorStyles[color]}
            rounded-arc-full
          `}
          initial={animated ? { width: 0 } : false}
          animate={{ width: `${percentage}%` }}
          transition={
            animated
              ? { type: "spring", stiffness: 100, damping: 20 }
              : { duration: 0 }
          }
        />
      </div>
    </div>
  );
};

export default Progress;









